const _ = require("lodash");
const EventEmitter = require("events");
const ActiveTimeouts = require("./ActiveTimeouts");
const SocketPing = require("./SocketPing");
const {FLAGS} = require("../../util/GlobalFlags");

const NEW_SOCKET_GRACE_PERIOD = 2000;

// Class wrapping all logic for BA socket communications that are not specific
// to any particular endpoint. Contains timeout handling, pinging, and
// connection watching.

//
// Private helpers
//

// Safe to call more than once for a socket.
const unbindSocketListeners = function(sw, socket) {
	sw.socketListeners.forEach(([name, cb]) => socket.off(name, cb));
};

const processWaitingEmitQueue = function(sw) {
	const queue = sw.waitingEmitQueue;
	sw.waitingEmitQueue = [];
	queue.forEach(({params, cb}) => {
		sw.emit(params, cb);
	});
};

const createSocket = function(sw) {
	if (sw.waitingToRecreate) {
		return;
	}
	const newSocket = sw.createSocket();
	sw.connectCount = 0;
	sw.recreateNeeded = false;
	sw.socket = newSocket;
	if (newSocket) {
		sw.socketListeners.forEach(([name, cb]) => newSocket.on(name, cb));
		waitForSocketToConnect(newSocket, NEW_SOCKET_GRACE_PERIOD, () => {
			updateTimeoutActiveState(sw);
			updateConnectedStatus(sw);
		});
	}
	processWaitingEmitQueue(sw);
};

// Allows timeout ms for the new socket to connect. If no connect event happens
// in that time, calls the onFail function.
const waitForSocketToConnect = function(newSocket, timeout, onFail) {
	const onConnect = () => clearTimeout(graceTO);
	newSocket.once("connect", onConnect);
	const graceTO = setTimeout(() => {
		newSocket.off("connect", onConnect);
		onFail();
	}, timeout);
};

const destroyCurrentSocket = function(sw) {
	const oldSocket = sw.socket;
	sw.recreateNeeded = false;
	sw.socket = null;
	if (oldSocket) {
		unbindSocketListeners(sw, oldSocket);
		oldSocket.disconnect();
	}
};

const destroySpecificSocket = function(sw, badSocket) {
	if (sw.socket === badSocket) {
		destroyCurrentSocket(sw);
		createSocket(sw);
	} else {
		unbindSocketListeners(sw, badSocket);
		badSocket.disconnect();
		_.pull(sw.dyingSockets, badSocket);
	}
};

const replaceSocket = function(sw, oldSocket) {
	if (sw.socket === oldSocket && oldSocket) {
		unbindSocketListeners(sw, oldSocket);
		sw.dyingSockets.push(oldSocket);
		oldSocket.on("disconnect", () => destroySpecificSocket(sw, oldSocket));
		createSocket(sw);
	}
};

// Returns either a string or null.
const getError = function(response, socketEvent) {
	const defaultError = `No recognized ${socketEvent} response returned`;
	if (!response || typeof response === "string") {
		return response || defaultError;
	} else if (
		typeof response.statusCode === "number" &&
		response.statusCode === 429
	) {
		return "E_TOO_MANY_REQUESTS";
	} else if (
		typeof response.statusCode === "number" &&
		response.statusCode === 403
	) {
		return "E_NOT_LOGGED_IN";
	} else if (
		typeof response.statusCode === "number" &&
		response.statusCode >= 400
	) {
		return (
			response.statusCode +
			": " +
			(response.message || response.error || "Unknown error")
		);
	} else if (response.success !== false) {
		return null;
	} else if (typeof response.error === "string" && response.error) {
		return response.error;
	} else if (Array.isArray(response.errors) && response.errors.length) {
		return response.errors.join("|");
	} else {
		return defaultError;
	}
};

const augmentWithTimeout = function(sw, cb, duration, failEarly) {
	let timeoutID = null;
	let called = false;

	const realCb = response => {
		if (!called) {
			called = true;
			sw.aTO.clearTimeout(timeoutID);
			cb(response);
		}
	};
	const onTimeout = () => {
		if (called) {
			// Callback was called; not actually a timeout.
		} else if (sw.noPingOnTimeout) {
			called = true;
			cb("E_REQUEST_TIMEOUT");
		} else if (failEarly) {
			called = true;
			cb("E_REQUEST_TIMEOUT");
			sw.socketPing.waitOnPing();
		} else {
			sw.socketPing.waitOnPing(() => {
				if (!called) {
					called = true;
					cb("E_REQUEST_TIMEOUT_WITH_PING");
				}
			});
		}
	};

	timeoutID = sw.aTO.setTimeout(onTimeout, duration);
	return realCb;
};

const updateTimeoutActiveState = function(sw) {
	const connected = sw.socket && sw.socket.connected;
	sw.aTO.setActive(sw.pageHasFocus && connected);
};

const updateConnectedStatus = function(sw) {
	const connected = sw.socket && sw.socket.connected;
	const pinging = sw.socketPing.isPinging();
	const newState = {
		connected: connected && !pinging,
		pingingSocket: connected && pinging,
	};
	const oldState = sw.lastConnectedState;
	if (
		!oldState ||
		oldState.connected !== newState.connected ||
		oldState.pingingSocket !== newState.pingingSocket
	) {
		sw.lastConnectedState = newState;
		sw.aTO.setTimeout(() => sw.ev.emit("connected", newState), 0, {
			alwaysActive: true,
		});
	}
};

//
// SocketWrapper class
//

/**
 * The only required property is createSocket, which returns a new fresh socket
 * object that has EventEmitter methods, aliases off to removeListener, has a
 * connected property, and has a disconnect method.
 *
 * ---
 *
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const SocketWrapper = function(props = {}) {
	if (!(this instanceof SocketWrapper)) {
		return new SocketWrapper(props);
	}
	this.createSocket = props.createSocket;
	this.timeout = props.timeout || 10000;

	// This option disables the pinging we do to try to detect disconnects that
	// socketio doesn't detect. It may cause more issues than it solves. At the
	// time of writing BA always sets this to true.
	this.noPingOnTimeout = !!props.noPingOnTimeout;

	this.socket = null;
	this.dyingSockets = [];
	this.recreateNeeded = false;
	this.waitingToRecreate = false;
	this.waitingEmitQueue = [];
	this.connectCount = 0;

	this.socketListeners = [];
	createSocket(this);

	this.aTO = new ActiveTimeouts({mockingTime: props.mockingTime});
	this.socketPing = new SocketPing({
		getSocket: () => this.socket,
		aTO: this.aTO,
	});
	this.pageHasFocus = true;

	// Set up disconnect watching
	this.ev = new EventEmitter();
	this.lastConnectedState = null;

	this.onSocketEvent("disconnect", () => {
		updateTimeoutActiveState(this);
		updateConnectedStatus(this);
	});
	this.onSocketEvent("connect", () => {
		this.connectCount += 1;
		this.socketPing.sendPing(); // Noop if not pinging
		updateTimeoutActiveState(this);
		updateConnectedStatus(this);
	});

	this.onSocketEvent("shuttingDown", () => {
		replaceSocket(this, this.socket);
	});

	this.onSocketEvent("alreadyShuttingDown", () => {
		this.setNeedsRecreate();
		this.flushRecreate();
	});

	updateTimeoutActiveState(this);
	updateConnectedStatus(this);
	this.socketPing.onStateChange(() => {
		updateConnectedStatus(this);
	});
};

// If mockingTime was true on construction, use this to advance an internal
// timer used to determine when setTimeouts should be called.
SocketWrapper.prototype.advanceMockTime = function(ms) {
	return this.aTO.advanceMockTime(ms);
};

// Like the global setTimeout, but can be paused by disconnects or the page
// losing focus, and can be mocked. Uses different IDs from the global.
SocketWrapper.prototype.setTimeout = function(f, ms, options) {
	return this.aTO.setTimeout(f, ms, options);
};

// Like the global clearTimeout, but for this class's setTimeout IDs.
SocketWrapper.prototype.clearTimeout = function(id) {
	return this.aTO.clearTimeout(id);
};

// Pass true or false. If the page does not have focus, all setTimeout calls
// used by the socket will be paused.
SocketWrapper.prototype.setPageFocus = function(newVal) {
	this.pageHasFocus = !!newVal;
	updateTimeoutActiveState(this);
};

// Set that the socket should be recreated. Calls to flushRecreate are a noop
// unless this has been called since the last socket creation.
SocketWrapper.prototype.setNeedsRecreate = function() {
	this.recreateNeeded = true;
};

// Destroy and create a new socket, if setNeedsRecreate has been called.
SocketWrapper.prototype.flushRecreate = function() {
	if (this.recreateNeeded) {
		destroyCurrentSocket(this);
		createSocket(this);
		updateTimeoutActiveState(this);
		updateConnectedStatus(this);
	}
};

// Call to manually start pinging. The socket is considered disconnected until
// a ping succeeds. cb is optional.
SocketWrapper.prototype.waitOnPing = function(cb) {
	if (this.noPingOnTimeout) return;
	return this.socketPing.waitOnPing(cb);
};

// Returns {connected: true|false, pingingSocket: true|false}.
// pingingSocket is true only if connected is false and means the socket didn't
// detect the disconnection.
SocketWrapper.prototype.getConnected = function() {
	return this.lastConnectedState;
};

// Returns the number of connect events fired on this socket. Can be used by
// external code to determine whether an event occurred as part of socket
// initialization or while in the middle of something.
SocketWrapper.prototype.getSocketConnectCount = function() {
	return this.connectCount;
};

// Listen to changes to getConnected. The listener gets passed the new value.
SocketWrapper.prototype.onConnectedChange = function(listener) {
	this.ev.on("connected", listener);
};

// Listen to an arbitrary event on the socket. The events are reattached when
// the socket is recreated.
SocketWrapper.prototype.onSocketEvent = function(name, listener) {
	this.socketListeners.push([name, listener]);
	this.socket && this.socket.on(name, listener);
};

// cb is optional. If provided, it will be passed (err, response) when socket
// emit finishes. err is either null or a nonempty string. params is documented
// just below.
SocketWrapper.prototype.emit = function(params = {}, cb) {
	const {
		// Required. Event name to emit on socket.
		socketEvent,
		// Optional. Payload to send with emitted event.
		socketPayload = null,
		// Optional. Use to override timeout duration (milliseconds). Set to false
		// to disable timeouts.
		timeout = this.timeout,
		// Optional. Set to true if not user-initiated. Changes timeout behavior.
		// With background true, it is possible the callback will never fire.
		background = false,
	} = params;
	const currSocket = this.socket;

	let realCb = response => {
		const error = getError(response, socketEvent);

		const {_destroySocket, _shuttingDown} = response || {};
		if (_destroySocket) {
			destroySpecificSocket(this, currSocket);
		} else if (_shuttingDown || error === "E_SERVER_SHUTDOWN") {
			replaceSocket(this, currSocket);
		}

		if (error === "E_SERVER_SHUTDOWN") {
			// Socket replaced above. Do the same call for the new socket.
			this.emit(params, cb);
		} else if (cb) {
			error ? cb(error, response) : cb(null, response);
		}
	};
	if (timeout && cb && !FLAGS.disableSocketTimeout) {
		realCb = augmentWithTimeout(this, realCb, timeout, !background);
	}

	if (!currSocket && this.waitingToRecreate) {
		this.waitingEmitQueue.push({params, cb});
	} else if (!currSocket) {
		cb && this.setTimeout(() => cb("E_NO_SOCKET"), 0);
	} else {
		currSocket.emit(socketEvent, socketPayload, realCb);
	}
};

module.exports = SocketWrapper;
